import { ZZFX_SAMPLE_RATE as SR } from '../lib/zzfx';
import { env, sweep } from './punch';

/**
 * A friend's invite (ADR-0167): a warm "you're wanted", soft enough for headphones. A little lift (a sine swept up over
 * a soft thump), then a quick rising major arpeggio of soft-mallet bells (G4 C5 E5, 65 ms apart) that lands on a ringing
 * G5 with the chord under it, and a light sprinkle of glitter over its tail. Every bell shimmers (`pair`). The same
 * every time: the sound players learn means someone wants to play.
 */
export function inviteSamples(): Float32Array {
  const out = new Float32Array(Math.round(LENGTH * SR));
  const add = (x: Float32Array, gain: number, at = 0): void => {
    const o = Math.round(at * SR);
    for (let i = 0; i < x.length && o + i < out.length; i++)
      out[o + i] = (out[o + i] as number) + gain * (x[i] as number);
  };
  /** A bell at `f` Hz from `at` s: its octave and twelfth over it, fading faster the higher they ring. */
  const bell = (f: number, at: number, gain: number, tau: number): void => {
    for (const [r, g, k] of PARTIALS) add(env(pair(0.9, f * r), MALLET, tau * k), gain * g, at);
  };
  // The lift.
  add(env(sweep(0.12, 150, 80, 0.03), 0.004, 0.045), 0.22);
  add(env(sweep(0.09, 300, 700, 0.025), 0.008, 0.028), 0.1);
  // The arpeggio, landing on G5 with the chord ringing under it.
  bell(392, 0.03, 0.5, 0.09);
  bell(523.25, 0.095, 0.5, 0.09);
  bell(659.25, 0.16, 0.48, 0.1);
  bell(783.99, 0.225, 0.58, 0.3);
  bell(523.25, 0.225, 0.2, 0.24);
  bell(659.25, 0.225, 0.18, 0.24);
  // Glitter.
  for (const [f, at, g] of GLITTER) add(env(pair(0.12, f), 0.003, 0.035), g, at);
  // Levelled to its loudness, its ring faded out over its last FADE s.
  let peak = 0;
  let sum = 0;
  for (const v of out) {
    peak = Math.max(peak, Math.abs(v));
    sum += v * v;
  }
  let end = out.length;
  while (end > 1 && Math.abs(out[end - 1] as number) < 0.002 * peak) end--;
  const gain = RMS / Math.sqrt(sum / end);
  const x = out.slice(0, end).map((v) => v * gain);
  const fade = Math.min(Math.round(FADE * SR), x.length);
  for (let i = 0; i < fade; i++) x[x.length - 1 - i] = (x[x.length - 1 - i] as number) * (i / fade);
  return x;
}

/** A bell's partials: ratio, level, decay (× its tau). */
const PARTIALS = [
  [1, 1, 1],
  [2, 0.22, 0.6],
  [3, 0.04, 0.35],
] as const;
/** A bell's attack (s): a soft mallet, no click in the ear. */
const MALLET = 0.006;
/** The glitter's pings: Hz, when (s), level. */
const GLITTER = [
  [1567.98, 0.3, 0.05],
  [1975.53, 0.355, 0.035],
  [1760, 0.42, 0.03],
  [2093, 0.5, 0.025],
] as const;
/** How long it lasts, and the fade its ring ends in (s). */
const LENGTH = 0.95;
const FADE = 0.15;
/** Its loudness over its length: about a menu click's to the ear, as long as a notification rings (`sfx invite`). */
const RMS = 0.085;

/**
 * A sine at `f` Hz with a quieter one 1.2 Hz over it: a struck bell's shimmer, never a full beat to silence (a ring that
 * swells back after it died reads as a wobble).
 */
function pair(secs: number, f: number): Float32Array {
  const b = new Float32Array(Math.round(secs * SR));
  for (let i = 0; i < b.length; i++)
    b[i] = 0.7 * Math.sin((2 * Math.PI * f * i) / SR) + 0.3 * Math.sin((2 * Math.PI * (f + 1.2) * i) / SR);
  return b;
}
